import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiResponseOfUserProfileDtoV2, AuthServiceV2, UserProfileDtoV2 } from 'api-client';
import { catchError, finalize, map, Observable, of, ReplaySubject, take, tap } from 'rxjs';
import { ImpersonationStore, LogService } from 'shared';

import { TokenStorageService } from '../services/token-storage.service';

function isBrowser(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined' &&
    typeof window.sessionStorage !== 'undefined'
  );
}

type StorageType = 'local' | 'session';
export type UserRole = 'SuperAdmin' | 'Admin' | null;

function isValidUserRole(value: unknown): value is UserRole {
  return value === 'SuperAdmin' || value === 'Admin' || value === null;
}

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private storageType: StorageType = 'local';

  setStorageType(rememberMe: boolean) {
    this.storageType = rememberMe ? 'local' : 'session';
  }

  private get storage(): Storage {
    return this.storageType === 'local' ? localStorage : sessionStorage;
  }

  private readonly USER_PROFILE_KEY = 'userProfile';
  private readonly USER_ROLE_KEY = 'userRole';
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly authService = inject(AuthServiceV2);
  private readonly logger = inject(LogService);
  private readonly impersonationStore = inject(ImpersonationStore);
  private readonly authReady$ = new ReplaySubject<void>(1);

  constructor() {
    this.restoreUserProfileFromStorage();
    queueMicrotask(() => this.restoreAuthStateFromStorage());
  }

  private storeUserProfile(profile: UserProfileDtoV2 | null) {
    if (!isBrowser()) return;
    if (profile) {
      this.storage.setItem(this.USER_PROFILE_KEY, JSON.stringify(profile));
    } else {
      this.storage.removeItem(this.USER_PROFILE_KEY);
    }
  }

  private storeUserRole(role: UserRole) {
    if (!isBrowser()) return;
    if (role) {
      this.storage.setItem(this.USER_ROLE_KEY, role);
    } else {
      this.storage.removeItem(this.USER_ROLE_KEY);
    }
  }

  public clearUserProfileFromAllStorages() {
    if (!isBrowser()) return;
    localStorage.removeItem(this.USER_PROFILE_KEY);
    localStorage.removeItem(this.USER_ROLE_KEY);
    sessionStorage.removeItem(this.USER_PROFILE_KEY);
    sessionStorage.removeItem(this.USER_ROLE_KEY);
  }

  public saveUserProfile(profile: UserProfileDtoV2 | null) {
    this.storeUserProfile(profile);
  }

  private restoreUserProfileFromStorage() {
    if (!isBrowser()) return;

    let stored = sessionStorage.getItem(this.USER_PROFILE_KEY);
    let storedRoleValue = sessionStorage.getItem(this.USER_ROLE_KEY);

    if (!stored) {
      stored = localStorage.getItem(this.USER_PROFILE_KEY);
      storedRoleValue = localStorage.getItem(this.USER_ROLE_KEY);
      this.storageType = 'local';
    } else {
      this.storageType = 'session';
    }

    if (stored) {
      try {
        const profile = JSON.parse(stored) as UserProfileDtoV2;
        this.setUser(profile);
        // Do NOT set isAuthenticated here — auth state is determined by the
        // cookie-backed profile request in restoreAuthStateFromStorage().
        if (storedRoleValue && isValidUserRole(storedRoleValue)) {
          this.setRole(storedRoleValue);
        }
      } catch {
        this.logger.error(
          'Failed to parse stored user profile',
          'AuthStore.restoreUserProfileFromStorage',
        );
        this.storage.removeItem(this.USER_PROFILE_KEY);
        this.storage.removeItem(this.USER_ROLE_KEY);
      }
    }
  }

  restoreUserProfileFromApi(): Observable<boolean> {
    this.setLoading(true);
    return this.authService.apiV2AuthProfileGet().pipe(
      tap((response: ApiResponseOfUserProfileDtoV2 | null) => {
        const user = response?.data ?? null;
        if (user) {
          this.setUser(user);
          this.setAuthenticated(true);
          this.storeUserProfile(user);
          // Extract role from profile's roles array
          const extractedRole = this.extractRoleFromProfile(user);
          if (extractedRole) {
            this.setRole(extractedRole);
          }
        } else {
          this.setAuthenticated(false);
          this.setUser(null);
          this.setRole(null);
          this.storeUserProfile(null);
          this.tokenStorage.clearToken();
        }
        this.setLoading(false);
      }),
      map((response) => !!response?.data),
      catchError(() => {
        this.setAuthenticated(false);
        this.setUser(null);
        this.setRole(null);
        this.setLoading(false);
        return of(false);
      }),
      finalize(() => this.authReady$.next()),
    );
  }

  waitForAuthState(): Observable<boolean> {
    return this.authReady$.pipe(
      take(1),
      map(() => this.isAuthenticated() && this.user() !== null),
    );
  }

  restoreAuthStateFromStorage(): void {
    this.restoreUserProfileFromApi().subscribe();
  }

  private readonly _user = signal<UserProfileDtoV2 | null>(null);
  private readonly _role = signal<UserRole>(null);
  private readonly _isAuthenticated = signal(false);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly user = this._user.asReadonly();
  readonly role = computed<UserRole>(() => {
    if (this.impersonationStore.isImpersonating()) {
      return (this.impersonationStore.impersonatedRole() as UserRole) ?? null;
    }
    return this._role();
  });
  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();

  // Role-based computed properties (derive from the public role which respects impersonation)
  readonly isSuperAdmin = () => this.role() === 'SuperAdmin';
  readonly isAdmin = () => this.role() === 'Admin' || this.role() === 'SuperAdmin';

  private extractRoleFromProfile(profile: UserProfileDtoV2): UserRole {
    if (profile.roles?.some((r) => r.name === 'SuperAdmin')) return 'SuperAdmin';
    if (profile.roles?.some((r) => r.name === 'Admin')) return 'Admin';
    return null;
  }

  setUser(user: UserProfileDtoV2 | null) {
    this._user.set(user);
  }

  setRole(role: UserRole) {
    this._role.set(role);
    this.storeUserRole(role);
  }

  setUserPhotoUrl(photoUrl: string) {
    const user = this._user();
    if (user) {
      this._user.set({ ...user, photoUrl });
    }
  }

  setUserPhoto(photoUrl: string, photoVersion?: number) {
    const user = this._user();
    if (user) {
      this._user.set({ ...user, photoUrl, photoVersion });
      this.saveUserProfile({ ...user, photoUrl, photoVersion });
    }
  }

  setAuthenticated(auth: boolean) {
    this._isAuthenticated.set(auth);
  }

  logout(notifyServer = true): void {
    this.impersonationStore.clearStorage();
    if (notifyServer) {
      this.authService
        .apiV2AuthLogoutPost({})
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
    this.tokenStorage.clearToken();
    this.clearUserProfileFromAllStorages();
    this.setAuthenticated(false);
    this.setUser(null);
    this.setRole(null);
  }

  setLoading(loading: boolean) {
    this._isLoading.set(loading);
  }

  setError(error: string | null): void {
    this._error.set(error);
  }

  clearError(): void {
    this._error.set(null);
  }

  clear() {
    this._user.set(null);
    this._role.set(null);
    this._isAuthenticated.set(false);
    this._error.set(null);
  }
}
